import React from "react";
import PropTypes from "prop-types";
import { i18n } from "../../core/i18n";

/**
 * Catches render-time errors so a single broken component degrades to a
 * readable message instead of unmounting the whole app to a blank page.
 *
 * Class component by necessity: componentDidCatch has no hook equivalent.
 * Copy is read with i18n.t rather than the useLocales hook: the fallback must
 * render even when the provider tree above it is what broke, and i18n.t is a
 * plain synchronous lookup on the bundled catalogs that never throws.
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// No error-reporting service is wired up yet; the console is the only
		// sink available. Replace this with a real reporter when one exists.
		console.error("[ErrorBoundary] uncaught render error:", error, errorInfo);
	}

	render() {
		if (!this.state.hasError) return this.props.children;
		if (this.props.fallback) return this.props.fallback;

		return (
			<div
				role="alert"
				className="min-h-screen bg-pp-bg flex flex-col items-center justify-center px-6 text-center"
			>
				<p className="font-mono text-xs uppercase tracking-pp-eyebrow text-pp-accent mb-4">
					{i18n.t("ui.errorEyebrow")}
				</p>
				<h1 className="font-sligoil text-2xl text-pp-ink mb-6">
					{i18n.t("ui.errorTitle")}
				</h1>
				<a
					href="/"
					className="font-mono text-xs uppercase tracking-pp-eyebrow border border-pp-accent text-pp-accent hover:bg-pp-accent hover:text-pp-bg px-4 py-2 transition-colors duration-150"
				>
					← {i18n.t("ui.errorBack")}
				</a>
			</div>
		);
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node,
	fallback: PropTypes.node,
};

export default ErrorBoundary;
